import { useMemo, useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { cn } from '../../lib/shadcn/utils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '../../lib/shadcn/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../lib/shadcn/table'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { useResolveStateDiscrepancy, useUndoStateResolution } from '../../hooks/backend/statements'
import { writeErrorMessage } from '../../utils/writeError'
import {
  type FleetStateCount,
  type ReconciliationRow,
  type StateResolutionRow,
  OPERATIONAL_STATE_ORDER,
  operationalStateConfig,
  humanize,
  formatDate,
} from '../../utils/statements'
import { OperationalStateChip, LifecycleChip } from './StateChips'

type Winner = 'sheet' | 'lifecycle'

function orderCounts(counts: FleetStateCount[]): FleetStateCount[] {
  const rank = (s: string) => {
    const i = OPERATIONAL_STATE_ORDER.indexOf(s)
    return i === -1 ? OPERATIONAL_STATE_ORDER.length : i
  }
  return [...counts].sort((a, b) => rank(a.operational_state) - rank(b.operational_state))
}

export function FleetStateStrip({
  counts,
  reconciliation,
  resolutions,
  selectedState,
  onSelectState,
  loading,
  canEdit,
  onResolved,
}: {
  counts: FleetStateCount[]
  reconciliation: ReconciliationRow[]
  resolutions: StateResolutionRow[]
  selectedState: string | null
  onSelectState: (state: string | null) => void
  loading: boolean
  canEdit: boolean
  onResolved: () => void
}) {
  const [open, setOpen] = useState(false)
  const [pendingPlate, setPendingPlate] = useState<string | null>(null)
  const [pendingWinner, setPendingWinner] = useState<Winner | null>(null)
  const [undoingId, setUndoingId] = useState<string | null>(null)

  const resolve = useResolveStateDiscrepancy()
  const undo = useUndoStateResolution()

  const ordered = useMemo(() => orderCounts(counts), [counts])
  const total = useMemo(() => counts.reduce((sum, c) => sum + c.count, 0), [counts])
  const discrepancyCount = reconciliation.length
  const hasResolutions = useMemo(() => resolutions.some((r) => !r.undone), [resolutions])

  const triggerDisabled = discrepancyCount === 0 && resolutions.length === 0
  const triggerLabel =
    discrepancyCount > 0
      ? `${discrepancyCount} state discrepanc${discrepancyCount === 1 ? 'y' : 'ies'}`
      : hasResolutions
        ? 'All reconciled'
        : 'No discrepancies'

  function clearPending() {
    setPendingPlate(null)
    setPendingWinner(null)
  }

  async function handleConfirm(plate: string, winner: Winner) {
    try {
      await resolve.trigger({ plate, winner }).result
      toast.success('Discrepancy resolved', { description: `${plate} reconciled.` })
      clearPending()
      onResolved()
    } catch (err) {
      toast.error('Could not resolve', { description: writeErrorMessage(err) })
      clearPending()
    }
  }

  async function handleUndo(eventId: string) {
    setUndoingId(eventId)
    try {
      await undo.trigger({ event_id: eventId }).result
      toast.success('Resolution reversed')
      onResolved()
    } catch (err) {
      toast.error('Could not reverse', { description: writeErrorMessage(err) })
    } finally {
      setUndoingId(null)
    }
  }

  function confirmSentence(row: ReconciliationRow, winner: Winner): string {
    if (winner === 'sheet') {
      return `Set lifecycle to ${humanize(row.sheet_operational_state)}. The owner sheet stays as it is.`
    }
    return `Set sheet state to ${humanize(row.lifecycle_status)}, dated today. Lifecycle stays as it is.`
  }

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border bg-card px-4 py-2.5">
      <button
        type="button"
        onClick={() => onSelectState(null)}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
          selectedState === null
            ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
            : 'border-border text-muted-foreground hover:text-foreground',
        )}
      >
        All vehicles
        <span className="tabular-nums font-semibold">{loading ? '…' : total}</span>
      </button>

      {ordered.map((c) => {
        const cfg = operationalStateConfig(c.operational_state)
        const active = selectedState === c.operational_state
        return (
          <button
            key={c.operational_state || 'unknown'}
            type="button"
            onClick={() => onSelectState(active ? null : c.operational_state)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-all',
              cfg.chip,
              active ? 'ring-2 ring-[hsl(var(--brand))] ring-offset-1 ring-offset-card' : 'opacity-90 hover:opacity-100',
            )}
          >
            <span className={cn('h-1.5 w-1.5 rounded-full', cfg.dot)} />
            {cfg.label}
            <span className="tabular-nums font-semibold">{c.count}</span>
          </button>
        )
      })}

      <div className="ml-auto">
        <Dialog
          open={open}
          onOpenChange={(next) => {
            // Clearing pending on close prevents a stale confirm strip from
            // lingering (or targeting a row that has since disappeared).
            if (!next) clearPending()
            setOpen(next)
          }}
        >
          <DialogTrigger asChild>
            <button
              type="button"
              disabled={triggerDisabled}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                discrepancyCount > 0
                  ? 'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200 dark:hover:bg-amber-900'
                  : triggerDisabled
                    ? 'cursor-default border-border text-muted-foreground'
                    : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              {triggerLabel}
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-5xl">
            <DialogHeader>
              <DialogTitle>State reconciliation queue</DialogTitle>
              <DialogDescription>
                Vehicles whose lifecycle status disagrees with the owner-sheet operational
                state. Review these — they are a data-quality queue, not an error to hide.
              </DialogDescription>
            </DialogHeader>

            {reconciliation.length === 0 ? (
              <div className="rounded-md border border-border py-8 text-center text-xs text-muted-foreground">
                Nothing left to reconcile.
              </div>
            ) : (
              <div className="max-h-[60vh] overflow-auto rounded-md border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Plate</TableHead>
                      <TableHead>Owner</TableHead>
                      <TableHead>Lifecycle</TableHead>
                      <TableHead>Sheet state</TableHead>
                      <TableHead>As of</TableHead>
                      <TableHead>Discrepancy</TableHead>
                      <TableHead className="text-right">Resolve</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reconciliation.map((r) => {
                      const isPending = pendingPlate === r.plate && pendingWinner !== null
                      return (
                        <TableRow key={r.plate}>
                          <TableCell className="font-mono text-xs font-medium">{r.plate}</TableCell>
                          <TableCell className="text-sm">{r.owner_name || '—'}</TableCell>
                          <TableCell>
                            <LifecycleChip status={r.lifecycle_status} />
                          </TableCell>
                          <TableCell>
                            <OperationalStateChip state={r.sheet_operational_state} />
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                            {formatDate(r.operational_state_as_of)}
                          </TableCell>
                          <TableCell className="text-sm text-foreground">{r.discrepancy}</TableCell>
                          <TableCell className="text-right">
                            {!canEdit ? (
                              <span className="text-xs text-muted-foreground">View only</span>
                            ) : isPending && pendingWinner ? (
                              <div className="flex flex-col items-end gap-1.5">
                                <span className="text-xs text-muted-foreground">
                                  {confirmSentence(r, pendingWinner)}
                                </span>
                                <div className="flex items-center justify-end gap-1.5">
                                  <Button
                                    size="sm"
                                    className="h-7 gap-1.5 px-2 text-xs"
                                    disabled={resolve.loading}
                                    onClick={() => handleConfirm(r.plate, pendingWinner)}
                                  >
                                    {resolve.loading && <Spinner className="h-3 w-3" />}
                                    Confirm
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-2 text-xs"
                                    disabled={resolve.loading}
                                    onClick={clearPending}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center justify-end gap-1.5">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 px-2 text-xs"
                                  onClick={() => {
                                    setPendingPlate(r.plate)
                                    setPendingWinner('sheet')
                                  }}
                                >
                                  Sheet is right
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 px-2 text-xs"
                                  onClick={() => {
                                    setPendingPlate(r.plate)
                                    setPendingWinner('lifecycle')
                                  }}
                                >
                                  Lifecycle is right
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            {resolutions.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-semibold text-foreground">Recently resolved</span>
                  <span className="text-xs text-muted-foreground">Last 30 days. Reversible.</span>
                </div>
                <div className="max-h-[60vh] overflow-auto rounded-md border border-border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Plate</TableHead>
                        <TableHead>Decision</TableHead>
                        <TableHead>Change</TableHead>
                        <TableHead>Resolved by</TableHead>
                        <TableHead>When</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {resolutions.map((r) => {
                        const change =
                          r.winner === 'sheet'
                            ? `${humanize(r.before_lifecycle_status)} → ${humanize(r.after_lifecycle_status)}`
                            : `${humanize(r.before_operational_state)} → ${humanize(r.after_operational_state)}`
                        return (
                          <TableRow key={r.event_id} className={cn(r.undone && 'opacity-60')}>
                            <TableCell className="font-mono text-xs font-medium">{r.plate}</TableCell>
                            <TableCell className="text-sm">
                              {r.winner === 'sheet' ? 'Sheet was right' : 'Lifecycle was right'}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                              {change}
                            </TableCell>
                            <TableCell className="text-sm">
                              {r.resolved_by_name || r.resolved_by_email}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                              {formatDate(r.resolved_at)}
                            </TableCell>
                            <TableCell className="text-right">
                              {r.undone ? (
                                <span className="text-xs text-muted-foreground">Reversed</span>
                              ) : canEdit ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 gap-1.5 px-2 text-xs"
                                  disabled={undo.loading && undoingId === r.event_id}
                                  onClick={() => handleUndo(r.event_id)}
                                >
                                  {undo.loading && undoingId === r.event_id && (
                                    <Spinner className="h-3 w-3" />
                                  )}
                                  Undo
                                </Button>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}
